import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";

export interface QuickBooksCompanyChoice {
  id: string;
  name: string;
  legalName: string | null;
  environment: "production" | "sandbox";
  homeCurrency: string | null;
  bookBasis: string | null;
  classesEnabled: boolean | null;
  locationsEnabled: boolean | null;
}

export interface QuickBooksConfirmationResult {
  ok: true;
  importStarted: boolean;
}

export function quickBooksConnectionNotice(importStarted: boolean | undefined): { kind: "success" | "error"; message: string } {
  if (importStarted === undefined) return { kind: "error", message: "QuickBooks connected. We couldn't confirm whether the expense import started. Check its connection card before trying again. Nothing was approved." };
  return importStarted === true
    ? { kind: "success", message: "QuickBooks connected. The expense import is queued. Nothing is approved for profit yet." }
    : { kind: "error", message: "QuickBooks connected, but the expense import did not start. Click Refresh overhead on its connection card to try again. Nothing was approved." };
}

export function QuickBooksCompanyDetails({ company }: { company: QuickBooksCompanyChoice }): React.ReactElement {
  return <>
    <div style={styles.company}>
      <strong>{company.name}</strong>
      {company.legalName && company.legalName !== company.name && <div>{company.legalName}</div>}
      <div style={styles.small}>Home currency: {company.homeCurrency ?? "Not provided by QuickBooks"}</div>
    </div>
    <p style={styles.copy}>Make sure these are the books for the business you are viewing. A QuickBooks company can contain more than one brand.</p>
    <p style={styles.copy}>We will read expenses for review. We will not add, change, or delete anything in QuickBooks.</p>
    <p style={styles.small}>For this review, costs count when they are recorded as expenses, not when cash is paid. Your QuickBooks reports stay unchanged.</p>
  </>;
}

export default function QuickBooksCompanyConfirm({ onClose, onConnected }: {
  onClose: () => void;
  onConnected: (result: QuickBooksConfirmationResult) => void;
}): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const company = useQuery({
    queryKey: ["quickbooks", "oauth-company"],
    queryFn: () => api.get<{ pending: boolean; company?: QuickBooksCompanyChoice }>("/integrations/quickbooks/oauth/company"),
    staleTime: 0,
    retry: false,
  });
  const connect = useMutation({
    mutationFn: () => {
      if (!company.data?.pending || !company.data.company) throw new Error("Company confirmation expired");
      return api.post<QuickBooksConfirmationResult>("/integrations/quickbooks/oauth/confirm-company", {
        companyId: company.data.company.id,
        reportBasis: "Accrual",
      });
    },
    onSuccess: (result) => {
      relockFinancialQueries(queryClient);
      queryClient.removeQueries({ queryKey: ["quickbooks", "oauth-company"] });
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      onConnected(result);
      onClose();
    },
  });
  const ready = company.data?.pending && company.data.company;
  // Only surface the API's purpose-written message, never a raw provider error.
  const failure = connect.error as { detail?: { error?: string } } | null;
  const error = typeof failure?.detail?.error === "string"
    ? failure.detail.error
    : "We couldn't connect this company. Nothing was approved. Try again.";
  return <div style={styles.backdrop}>
    <section role="dialog" aria-modal="true" aria-labelledby="quickbooks-company-title" style={styles.modal}>
      <div style={styles.header}>
        <h2 id="quickbooks-company-title" style={{ margin: 0, fontSize: 19 }}>Are these the right books?</h2>
        <button type="button" aria-label="Close company confirmation" onClick={onClose} disabled={connect.isPending} style={styles.close}>×</button>
      </div>
      {company.isLoading && <p style={styles.copy}>Checking the company you selected in QuickBooks…</p>}
      {company.isError && <p role="alert" style={styles.error}>We couldn't check that company. Close this window and start the QuickBooks connection again.</p>}
      {!company.isLoading && !company.isError && !ready && <p role="status" style={styles.copy}>This sign-in expired. Close this window and connect QuickBooks again.</p>}
      {ready && <QuickBooksCompanyDetails company={ready} />}
      {connect.isError && <p role="alert" style={styles.error}>{error}</p>}
      <div style={styles.actions}>
        <button type="button" onClick={onClose} disabled={connect.isPending} style={styles.cancel}>Cancel</button>
        <button type="button" onClick={() => connect.mutate()} disabled={!ready || connect.isPending} style={styles.submit}>
          {connect.isPending ? "Connecting…" : "Connect these books"}
        </button>
      </div>
    </section>
  </div>;
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modal: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 24, width: 500, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 },
  close: { background: "transparent", color: "var(--body)", border: 0, fontSize: 24, cursor: "pointer" },
  company: { background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, padding: 14, lineHeight: 1.6 },
  copy: { color: "var(--body)", fontSize: 13, lineHeight: 1.6 },
  small: { color: "var(--faint)", fontSize: 12, lineHeight: 1.6 },
  error: { color: "var(--bad)", fontSize: 13, lineHeight: 1.5 },
  actions: { display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 8, marginTop: 20 },
  cancel: { background: "transparent", color: "var(--body)", border: "1px solid var(--border)", borderRadius: 6, padding: "9px 15px", cursor: "pointer" },
  submit: { background: "var(--accent)", color: "var(--accent-contrast)", border: 0, borderRadius: 6, padding: "9px 15px", fontWeight: 650, cursor: "pointer" },
};
